import { Component, OnDestroy } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { take, takeUntil } from "rxjs/operators";
import { ReplaySubject } from "rxjs";
import { ProjectService } from "../../../../shared/services/project.service";
import { Field } from "../../../../models/field";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { forkJoin } from "rxjs";
import { Project } from "../../../../models/project";
import { NzMessageService } from "ng-zorro-antd/message";

@Component({
    selector: "app-project-information",
    templateUrl: "./project-information.component.html",
    styleUrls: ["./project-information.component.less"],
    standalone: false
})
export class ProjectInformationComponent implements OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1); // used to unsubscribe to all subscriptions on destroy
  projectInfoControl: FormGroup = this.fb.group({
    description: this.fb.control("", []),
    displayVariables: this.fb.array([]),
    field: this.fb.control({}, [Validators.required]),
    name: this.fb.control("", [Validators.required]),
  });
  fields: Field[] = [];
  isLoading: boolean = false;

  // We can expand this list later if needed
  commonModelTypes: string[] = ["Vp", "vdiff", "DenChange", "RWI_Perturb"];

  filteredModelTypes: string[] = [...this.commonModelTypes];
  currentProject: Project;

  onModelTypeSearch(value: string): void {
    if (!value || value.trim() === "") {
      this.filteredModelTypes = [...this.commonModelTypes];
      return;
    }

    const searchValue = value.toLowerCase();
    this.filteredModelTypes = this.commonModelTypes.filter((type) =>
      type.toLowerCase().includes(searchValue)
    );

    // If the typed value is not in the list, add it as an option
    if (
      !this.commonModelTypes.some((type) => type.toLowerCase() === searchValue)
    ) {
      this.filteredModelTypes = [value, ...this.filteredModelTypes];
    }
  }

  get displayVariablesFormArray(): FormArray {
    return this.projectInfoControl.get("displayVariables") as FormArray;
  }

  // Check if a model type is zero-centered (everything except vp for now)
  isZeroCentered(modelType: string): boolean {
    return modelType !== "Vp" && modelType.trim() !== "";
  }

  createDisplayVariableFormGroup(
    modelType: string = "",
    minValue: number | null = null,
    maxValue: number | null = null
  ): FormGroup {
    const isZeroCentered = this.isZeroCentered(modelType);

    // For zero-centered types, extract the single value from min/max
    // If min = -max, use max as the single value
    let singleValue: number | null = null;
    if (isZeroCentered && minValue !== null && maxValue !== null) {
      // Check if it's symmetric (zero-centered)
      if (Math.abs(minValue + maxValue) < 0.0001) {
        // Use small epsilon for floating point comparison
        singleValue = Math.abs(maxValue); // Use absolute value of max
      } else {
        // If not symmetric, use max as the value (fallback)
        singleValue = Math.abs(maxValue);
      }
    }

    const formGroup = this.fb.group(
      {
        modelType: this.fb.control(modelType, [Validators.required]),
        minValue: this.fb.control(isZeroCentered ? null : minValue),
        maxValue: this.fb.control(isZeroCentered ? null : maxValue),
        singleValue: this.fb.control(isZeroCentered ? singleValue : null),
      },
      { validators: this.displayVariableValidator }
    );

    if (isZeroCentered) {
      formGroup.get("minValue")?.disable();
      formGroup.get("maxValue")?.disable();
      formGroup.get("singleValue")?.enable();
    } else {
      formGroup.get("singleValue")?.disable();
      formGroup.get("minValue")?.enable();
      formGroup.get("maxValue")?.enable();
    }

    return formGroup;
  }

  // Custom validator to ensure min is not greater than max for each display variable
  displayVariableValidator(control: AbstractControl): ValidationErrors | null {
    const modelType = control.get("modelType")?.value;
    const isZeroCentered =
      modelType !== "Vp" && modelType && modelType.trim() !== "";

    if (isZeroCentered) {
      // For zero-centered types, singleValue must be positive
      const singleValue = control.get("singleValue")?.value;
      if (
        singleValue !== null &&
        singleValue !== "" &&
        Number(singleValue) <= 0
      ) {
        return { singleValueError: true };
      }
    } else {
      // For non-zero-centered types (Vp), validate min < max
      const minValue = control.get("minValue")?.value;
      const maxValue = control.get("maxValue")?.value;

      if (
        minValue !== null &&
        maxValue !== null &&
        minValue !== "" &&
        maxValue !== "" &&
        minValue > maxValue
      ) {
        return { minMaxError: true };
      }
    }

    return null;
  }

  addDisplayVariable(): void {
    const formGroup = this.createDisplayVariableFormGroup();

    formGroup
      .get("modelType")
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const index =
          this.displayVariablesFormArray.controls.indexOf(formGroup);
        if (index >= 0) {
          this.onModelTypeChange(index);
        }
      });

    this.displayVariablesFormArray.push(formGroup);
  }

  removeDisplayVariable(index: number): void {
    this.displayVariablesFormArray.removeAt(index);
  }

  // Handle model type change - update form controls based on zero-centered status
  onModelTypeChange(index: number): void {
    const control = this.displayVariablesFormArray.at(index);
    const modelType = control.get("modelType")?.value;

    if (!modelType || modelType.trim() === "") {
      // If model type is cleared, disable all value fields
      control.get("minValue")?.disable();
      control.get("maxValue")?.disable();
      control.get("singleValue")?.disable();
      return;
    }

    const isZeroCentered = this.isZeroCentered(modelType);

    if (isZeroCentered) {
      // For zero-centered types: use singleValue, clear min/max
      const currentSingleValue = control.get("singleValue")?.value;
      const currentMin = control.get("minValue")?.value;
      const currentMax = control.get("maxValue")?.value;

      // If switching from non-zero-centered, try to extract single value from min/max
      let newSingleValue = currentSingleValue;
      if (
        currentMin !== null &&
        currentMax !== null &&
        currentSingleValue === null
      ) {
        // Check if it's symmetric
        if (Math.abs(currentMin + currentMax) < 0.0001) {
          newSingleValue = Math.abs(currentMax);
        }
      }

      control.patchValue(
        {
          minValue: null,
          maxValue: null,
          singleValue: newSingleValue,
        },
        { emitEvent: false }
      );

      control.get("minValue")?.disable();
      control.get("maxValue")?.disable();
      control.get("singleValue")?.enable();
    } else {
      // For non-zero-centered typesuse min/max, clear singleValue
      const currentMin = control.get("minValue")?.value;
      const currentMax = control.get("maxValue")?.value;

      control.patchValue(
        {
          singleValue: null,
          minValue: currentMin || null,
          maxValue: currentMax || null,
        },
        { emitEvent: false }
      );

      control.get("singleValue")?.disable();
      control.get("minValue")?.enable();
      control.get("maxValue")?.enable();
    }

    control.updateValueAndValidity();
  }

  constructor(
    private notificationService: NzNotificationService,
    private cachedProjectService: CachedProjectService,
    private fb: FormBuilder,
    private projectService: ProjectService,
    private message: NzMessageService
  ) {
    this.cachedProjectService.currentProject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        if (res) {
          // Clear existing display variables
          while (this.displayVariablesFormArray.length !== 0) {
            this.displayVariablesFormArray.removeAt(0);
          }

          if (
            res.displayVariables &&
            Object.keys(res.displayVariables).length > 0
          ) {
            Object.keys(res.displayVariables).forEach((modelType) => {
              const displayVar = res.displayVariables[modelType];
              const formGroup = this.createDisplayVariableFormGroup(
                modelType,
                displayVar.min,
                displayVar.max
              );

              formGroup
                .get("modelType")
                ?.valueChanges.pipe(takeUntil(this.destroyed$))
                .subscribe(() => {
                  const index =
                    this.displayVariablesFormArray.controls.indexOf(formGroup);
                  if (index >= 0) {
                    this.onModelTypeChange(index);
                  }
                });

              this.displayVariablesFormArray.push(formGroup);

              if (!this.commonModelTypes.includes(modelType)) {
                this.commonModelTypes.push(modelType);
                this.filteredModelTypes = [...this.commonModelTypes];
              }
            });
          }

          this.currentProject = res;
          this.projectInfoControl.patchValue({
            description: res?.description || "",
            field: res.field,
            name: res.name,
          });
        }
      });

    this.projectService
      .getAllFields()
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.fields = res.fields;
        },
        error: (err) => {
          console.log(err);
          this.notificationService.error(
            "Error",
            err.error?.error || "An error occurred while getting all the fields"
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  projectInformationSubmit() {
    console.log("submit project information form", this.projectInfoControl);
    if (this.isLoading) {
      return;
    }

    // Validate all display variables
    let hasValidationErrors = false;
    let validationErrorMessages: string[] = [];

    this.displayVariablesFormArray.controls.forEach((control, index) => {
      if (control.invalid) {
        hasValidationErrors = true;
        const modelType = control.get("modelType")?.value || `Row ${index + 1}`;
        if (control.hasError("minMaxError")) {
          validationErrorMessages.push(
            `${modelType}: Min value must be less than max value`
          );
        }
        if (control.hasError("singleValueError")) {
          validationErrorMessages.push(
            `${modelType}: Value must be greater than 0`
          );
        }
        if (control.get("modelType")?.hasError("required")) {
          validationErrorMessages.push(
            `Row ${index + 1}: Model type is required`
          );
        }
      }
    });

    if (!this.projectInfoControl.valid || hasValidationErrors) {
      if (validationErrorMessages.length > 0) {
        this.notificationService.error(
          "Validation Error",
          validationErrorMessages.join("; ")
        );
      } else {
        let errors = this.getFormErrors(this.projectInfoControl);
        this.notificationService.error(
          "Error",
          "Please check the form for errors: " + Object.keys(errors).join(", ")
        );
      }
      return;
    }

    this.isLoading = true;

    this.cachedProjectService.currentProject
      .pipe(take(1))
      .subscribe((currentProject) => {
        if (!currentProject) {
          this.notificationService.error("Error", "No project selected");
          this.isLoading = false;
          return;
        }

        // Prepare update data for project details (without display variables)
        const updateData = {
          id: currentProject.id,
          name: this.projectInfoControl.value.name,
          description: this.projectInfoControl.value.description,
          field: this.projectInfoControl.value.field,
        };

        // Collect display variables from form
        const displayVariablesToUpdate: {
          [key: string]: { min: number | null; max: number | null };
        } = {};
        const modelTypesInForm = new Set<string>();

        this.displayVariablesFormArray.controls.forEach((control) => {
          const modelType = control.get("modelType")?.value?.trim();
          if (modelType) {
            modelTypesInForm.add(modelType);
            const isZeroCentered = this.isZeroCentered(modelType);

            if (isZeroCentered) {
              let singleValue = control.get("singleValue")?.value;

              if (singleValue === "" || singleValue === null) {
                singleValue = null;
              } else {
                singleValue = Math.abs(Number(singleValue));

                if (singleValue > 0) {
                  displayVariablesToUpdate[modelType] = {
                    min: -singleValue,
                    max: singleValue,
                  };
                }
              }
            } else {
              // For non-zero-centered types use min/max directly
              let minValue = control.get("minValue")?.value;
              let maxValue = control.get("maxValue")?.value;

              if (minValue === "" || minValue === null) minValue = null;
              if (maxValue === "" || maxValue === null) maxValue = null;

              if (minValue !== null) minValue = Number(minValue);
              if (maxValue !== null) maxValue = Number(maxValue);

              if (minValue !== null || maxValue !== null) {
                displayVariablesToUpdate[modelType] = {
                  min: minValue,
                  max: maxValue,
                };
              }
            }
          }
        });

        // First, get existing display variables to identify deletions
        this.projectService
          .getDisplayVariablesForProject(currentProject.id)
          .pipe(take(1))
          .subscribe({
            next: (existingDisplayVars) => {
              // Find display variables that need to be deleted
              const displayVarsToDelete = existingDisplayVars.filter(
                (dv) => !modelTypesInForm.has(dv.key)
              );

              // Update project details first
              this.projectService
                .updateProjectDetails(updateData)
                .pipe(take(1))
                .subscribe({
                  next: (response) => {
                    // Delete removed display variables
                    const deleteObservables = displayVarsToDelete.map((dv) =>
                      this.projectService.deleteDisplayVariable(dv.id)
                    );

                    if (deleteObservables.length > 0) {
                      forkJoin(deleteObservables)
                        .pipe(take(1))
                        .subscribe({
                          next: () => {
                            // After deletions, update/create remaining display variables
                            this.updateDisplayVariablesAfterDeletion(
                              currentProject,
                              updateData,
                              displayVariablesToUpdate
                            );
                          },
                          error: (deleteErr) => {
                            console.error(
                              "Error deleting display variables:",
                              deleteErr
                            );
                            // Continue with updates even if deletions fail
                            this.updateDisplayVariablesAfterDeletion(
                              currentProject,
                              updateData,
                              displayVariablesToUpdate
                            );
                          },
                        });
                    } else {
                      // No deletions needed, proceed with updates
                      this.updateDisplayVariablesAfterDeletion(
                        currentProject,
                        updateData,
                        displayVariablesToUpdate
                      );
                    }
                  },
                  error: (err) => {
                    console.error("Error updating project:", err);
                    this.notificationService.error(
                      "Error",
                      err.error?.error || "Failed to update project information"
                    );
                    this.isLoading = false;
                  },
                });
            },
            error: (err) => {
              console.error("Error fetching display variables:", err);
              // If we can't fetch existing vars, proceed with updates only
              this.updateDisplayVariablesAfterDeletion(
                currentProject,
                updateData,
                displayVariablesToUpdate
              );
            },
          });
      });
  }

  private updateDisplayVariablesAfterDeletion(
    currentProject: any,
    updateData: any,
    displayVariablesToUpdate: {
      [key: string]: { min: number | null; max: number | null };
    }
  ): void {
    if (Object.keys(displayVariablesToUpdate).length > 0) {
      this.projectService
        .updateDisplayVariables(currentProject.id, displayVariablesToUpdate)
        .pipe(take(1))
        .subscribe({
          next: (displayVarResponses) => {
            this.cachedProjectService.updateProjectDetails({
              ...currentProject,
              ...updateData,
              displayVariables: displayVariablesToUpdate,
            });
            this.cachedProjectService.clearProjectsCache();
            this.notificationService.success(
              "Success",
              "Project information and display variables updated successfully"
            );
            this.isLoading = false;
          },
          error: (displayVarErr) => {
            console.error("Error updating display variables:", displayVarErr);
            // Still update project details even if display variable update fails
            this.cachedProjectService.updateProjectDetails({
              ...currentProject,
              ...updateData,
            });
            this.cachedProjectService.clearProjectsCache();
            this.notificationService.warning(
              "Partial Success",
              "Project information updated, but display variables update failed: " +
                (displayVarErr.error?.error || "Unknown error")
            );
            this.isLoading = false;
          },
        });
    } else {
      // No display variables to update, just update project details
      this.cachedProjectService.updateProjectDetails({
        ...currentProject,
        ...updateData,
      });
      this.cachedProjectService.clearProjectsCache();
      this.notificationService.success(
        "Success",
        "Project information updated successfully"
      );
      this.isLoading = false;
    }
  }

  fieldCompareFn = (o1: Field, o2: Field): boolean =>
    o1 && o2 ? o1.id === o2.id : o1 === o2;

  getFormErrors(form: any) {
    if (!form.controls) {
      // Return FormControl errors or null
      return form.errors ?? null;
    }
    const groupErrors = form.errors;
    const formErrors = groupErrors ? { groupErrors } : {};
    Object.keys(form.controls).forEach((key) => {
      const error = this.getFormErrors(form.get(key));
      if (error !== null) {
        formErrors[key] = error;
      }
    });
    return Object.keys(formErrors).length > 0 ? formErrors : null;
  }

  enableArchiving() {
    this.isLoading = true;
    this.projectService
      .setProjectArchiving({
        projectIds: [this.currentProject.id],
        archive_enabled: true,
      })
      .subscribe(
        async (res) => {
          this.message.success(
            `Archiving enabled for ${this.currentProject.name}`
          );
          this.cachedProjectService.clearProjectsCache();
          this.cachedProjectService.clearProjectCachedById(
            this.currentProject.id
          );
          this.currentProject = await this.cachedProjectService
            .getProjectById(this.currentProject.id)
            .pipe(take(1))
            .toPromise();
          this.isLoading = false;
        },
        (err) => {
          this.isLoading = false;
          console.error(err);
          this.message.error(`An error occured.`);
        }
      );
  }
  disableArchiving() {
    this.isLoading = true;
    this.projectService
      .setProjectArchiving({
        projectIds: [this.currentProject.id],
        archive_enabled: false,
      })
      .subscribe(
        async (res) => {
          this.message.success(
            `Archiving disabled for ${this.currentProject.name}`
          );
          this.cachedProjectService.clearProjectsCache();
          this.cachedProjectService.clearProjectCachedById(
            this.currentProject.id
          );
          this.currentProject = await this.cachedProjectService
            .getProjectById(this.currentProject.id)
            .pipe(take(1))
            .toPromise();
          this.isLoading = false;
        },
        (err) => {
          this.isLoading = false;
          console.error(err);
          this.message.error(`An error occured.`);
        }
      );
  }
}
